// Audio effects utility API layer
// This file maintains the public API while delegating to the underlying implementation

import { SoundManager, getSoundManager } from './sound-manager';
import { SOUND_EFFECTS, registerPredefinedSounds } from './predefined-sounds';
import { isMobile, isIOS } from '../../device';

// Get the singleton instance of the sound manager
const soundManager = getSoundManager();

// Register all predefined sounds on first import
registerPredefinedSounds(soundManager);

// Track initialization status
let isAudioInitialized = false;
let audioInitPromise: Promise<boolean> | null = null;
let audioInitAttempts = 0;
const MAX_INIT_ATTEMPTS = 3;

// Enable high priority button down sound immediately
soundManager.preloadSound(SOUND_EFFECTS.BUTTON_DOWN.id, true)
  .catch(e => console.warn("Failed to preload initial button down sound:", e));

// Initialize audio system with more verbose logging
export const initAudio = async (): Promise<boolean> => {
  // If already initialized, return immediately
  if (isAudioInitialized) {
    console.log("Audio already initialized, skipping");
    return true;
  }
  
  // If initialization is in progress, return existing promise
  if (audioInitPromise) {
    console.log("Audio initialization already in progress, reusing existing promise");
    return audioInitPromise;
  }

  // Limit initialization attempts to prevent infinite loops
  if (audioInitAttempts >= MAX_INIT_ATTEMPTS) {
    console.warn(`Audio initialization failed after ${MAX_INIT_ATTEMPTS} attempts, giving up`);
    return false;
  }
  
  audioInitAttempts++;
  console.log(`Initializing audio system... (Mobile: ${isMobile()}, iOS: ${isIOS()}) [Attempt ${audioInitAttempts}/${MAX_INIT_ATTEMPTS}]`);

  // Create a new initialization promise
  audioInitPromise = (async () => {
    try {
      // Initialize the sound manager
      const result = await soundManager.initialize();
      console.log("Audio initialization result:", result ? "SUCCESS" : "FAILED");
      
      // If successful, start preloading key sounds
      if (result) {
        isAudioInitialized = true;
        
        // Preload button sounds immediately for better first-click experience
        console.log("Starting to preload button sounds...");
        try {
          // Button down first, highest priority
          await soundManager.preloadSound(SOUND_EFFECTS.BUTTON_DOWN.id, true);
          
          // Others can load in parallel after
          Promise.all([
            soundManager.preloadSound(SOUND_EFFECTS.BUTTON_UP.id, true),
            soundManager.preloadSound(SOUND_EFFECTS.BUTTON_CLICK.id, true)
          ]).catch(e => console.warn("Failed to preload some button sounds:", e));
        } catch (e) {
          console.warn("Failed to preload some button sounds:", e);
        }
        
        // Start preloading other sounds in the background
        soundManager.preloadSound(SOUND_EFFECTS.FIREWORKS.id, false)
          .catch(e => console.warn("Failed to preload fireworks sound:", e));
        
        // Log the registration status of sounds
        const soundIds = soundManager.getSoundIds();
        console.log(`Registered sounds (${soundIds.length}):`, soundIds.join(', '));
      }
      
      return result;
    } catch (error) {
      console.error("Error during audio initialization:", error);
      return false;
    } finally {
      // Clear the promise so new attempts can be made if needed
      audioInitPromise = null;
    }
  })();
  
  return audioInitPromise;
};

// Enhanced debug function to play fireworks sound with logging
export const playFireworksSound = (): void => {
  console.log("Playing fireworks sound");
  try {
    soundManager.playSound(SOUND_EFFECTS.FIREWORKS.id);
  } catch (error) {
    console.error("Error playing fireworks sound:", error);
  }
};

// Enhanced debug function to play button click sound with logging - for VOTING ACTIONS ONLY
export const playButtonSound = (): void => {
  console.log("Playing button click sound - VOTING ACTION");
  try {
    soundManager.playSound(SOUND_EFFECTS.BUTTON_CLICK.id);
  } catch (error) {
    console.error("Error playing button click sound:", error);
  }
};

// Enhanced debug function to play button down sound with logging - for VOTING ACTIONS ONLY
export const playButtonDownSound = (): void => {
  console.log("Playing button DOWN sound - VOTING ACTION");
  try {
    // Try to ensure audio is initialized for first interaction
    if (!isAudioInitialized) {
      initAudio().catch(() => {});
    }
    
    soundManager.playSound(SOUND_EFFECTS.BUTTON_DOWN.id);
  } catch (error) {
    console.error("Error playing button down sound:", error);
  }
};

// Enhanced debug function to play button up sound with logging - for VOTING ACTIONS ONLY
export const playButtonUpSound = (): void => {
  console.log("Playing button UP sound - VOTING ACTION");
  try {
    soundManager.playSound(SOUND_EFFECTS.BUTTON_UP.id);
  } catch (error) {
    console.error("Error playing button up sound:", error);
  }
};

// Expose a method to register custom sounds
export const registerSound = (id: string, url: string, volume = 1.0): void => {
  soundManager.registerSound(id, url, volume);
};

// Add a method to play any registered sound by ID
export const playSound = (id: string): void => {
  soundManager.playSound(id);
};

// Export the singleton for advanced use cases
export { getSoundManager };

// Re-export the sound effect definitions for external use
export { SOUND_EFFECTS };
